/**
 * Chatwoot configuration for Atlas UI.
 */
export const CHATWOOT_BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL || "/cw";
export const CHATWOOT_APP_URL = `${CHATWOOT_BASE_URL}/app`;

/**
 * Fallback Chatwoot account id used only when the live account cannot be
 * resolved (see {@link fetchChatwootAccountId}). Single-tenant deployments
 * default to account 1; override via VITE_CHATWOOT_ACCOUNT_ID when it differs.
 */
export const CHATWOOT_ACCOUNT_ID = import.meta.env.VITE_CHATWOOT_ACCOUNT_ID || "1";

/** Build the Chatwoot Settings → Inboxes deep link for a given account. */
export function buildChatwootInboxesUrl(accountId: string | number): string {
  return `${CHATWOOT_APP_URL}/accounts/${accountId}/settings/inboxes/list`;
}

/**
 * Chatwoot's `/auth/validate_token` needs devise-token-auth headers, not the
 * session cookie (a plain cookie call 401s). The dashboard keeps those headers
 * in a JS-readable cookie; this is the subset we replay onto the request.
 */
interface ChatwootSessionInfo {
  "access-token"?: string;
  "token-type"?: string;
  client?: string;
  expiry?: string | number;
  uid?: string;
}

/** Cookie Chatwoot stores its devise-token-auth headers in (non-httpOnly). */
const CHATWOOT_SESSION_COOKIE = "cw_d_session_info";

/** Read a browser cookie by name, URL-decoded, or null if absent. */
function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/** One account the logged-in user belongs to, from `validate_token`. */
interface ChatwootAccount {
  id?: string | number;
  name?: string;
}

/**
 * Subset of the `/auth/validate_token` `data` payload we rely on: the user's
 * default `account_id` plus the full list of accounts they belong to (each
 * carrying the account `name` we match on).
 */
interface ChatwootValidateTokenData {
  account_id?: string | number;
  accounts?: ChatwootAccount[];
}

/** Memoised in-flight/resolved validate_token lookup so we hit the API once. */
let validateTokenPromise: Promise<ChatwootValidateTokenData> | null = null;

/** Normalise an account/entity name for tolerant matching (trim + casefold). */
function normaliseName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Fetch (and memoise) the logged-in user's `validate_token` payload.
 *
 * Chatwoot's API authenticates via devise-token-auth headers (not the session
 * cookie), so we lift those headers out of the dashboard's `cw_d_session_info`
 * cookie — readable because Chatwoot is embedded same-origin under
 * {@link CHATWOOT_BASE_URL} — and replay them against `/auth/validate_token`.
 */
function fetchValidateTokenData(): Promise<ChatwootValidateTokenData> {
  if (validateTokenPromise) return validateTokenPromise;

  validateTokenPromise = (async () => {
    try {
      const raw = readCookie(CHATWOOT_SESSION_COOKIE);
      if (!raw) throw new Error("chatwoot session cookie not found");
      const session: ChatwootSessionInfo = JSON.parse(raw);

      const headers: Record<string, string> = { Accept: "application/json" };
      if (session["access-token"]) headers["access-token"] = session["access-token"];
      if (session["token-type"]) headers["token-type"] = session["token-type"];
      if (session.client) headers.client = session.client;
      if (session.expiry != null) headers.expiry = `${session.expiry}`;
      if (session.uid) headers.uid = session.uid;

      const res = await fetch(`${CHATWOOT_BASE_URL}/auth/validate_token`, {
        credentials: "include",
        headers,
      });
      if (!res.ok) throw new Error(`validate_token failed: ${res.status}`);

      // Chatwoot returns { data: { account_id, accounts } }; some proxies wrap
      // it in { payload: { data } }, so accept either shape.
      const json = await res.json();
      const data = json?.payload?.data ?? json?.data;
      if (!data) throw new Error("validate_token response missing data");
      return data as ChatwootValidateTokenData;
    } catch (err) {
      // Reset so a later attempt can retry rather than caching the failure.
      validateTokenPromise = null;
      throw err;
    }
  })();

  return validateTokenPromise;
}

/**
 * Resolve the live Chatwoot account id for the logged-in user.
 *
 * When `accountName` is given we pick the account from the user's `accounts`
 * list whose name matches it — i.e. the account the entity whose "Inboxes"
 * button was clicked maps to. Duplicate names resolve to the first match.
 * Without a name (or when none matches) we fall back to the user's default
 * `account_id`.
 *
 * Cross-origin (e.g. `localhost` against the dev domain) the session cookie is
 * not visible and this falls back to {@link CHATWOOT_ACCOUNT_ID}, as does any
 * error.
 */
export async function fetchChatwootAccountId(accountName?: string): Promise<string> {
  try {
    const data = await fetchValidateTokenData();

    if (accountName) {
      const target = normaliseName(accountName);
      const match = data.accounts?.find(
        (acc) => acc?.name != null && normaliseName(acc.name) === target,
      );
      const matchId = match?.id;
      if (matchId !== undefined && matchId !== null && `${matchId}` !== "") {
        return `${matchId}`;
      }
    }

    const id = data.account_id;
    if (id === undefined || id === null || `${id}` === "") {
      throw new Error("validate_token response missing account_id");
    }
    return `${id}`;
  } catch {
    return CHATWOOT_ACCOUNT_ID;
  }
}

/**
 * Resolve the Chatwoot Settings → Inboxes deep link, targeting the account that
 * matches `accountName` (the clicked entity's name) when supplied.
 */
export async function fetchChatwootInboxesUrl(accountName?: string): Promise<string> {
  return buildChatwootInboxesUrl(await fetchChatwootAccountId(accountName));
}
